import { SectionHeader } from "@/components/common/section-components/SectionHeader";
import { AppColors } from "@/styles/colors";
import { Card, Flex, Grid, GridItem, Text } from "@chakra-ui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm, useWatch } from "react-hook-form";
import { motivesSchema } from "../schema/motives.schema";
import { SelectComponent } from "@/components/select/Select";
import { useEffect, useMemo, useState } from "react";
import { getTiposSolicitud } from "@/services/request-policies.service";
import { useNavigate } from "react-router-dom";
import { AppRoutes } from "@/constants/routes";
import { TextArea } from "@/components/text-area/TextArea";
import { handleCreateMotiveHandler } from "../schema/handler-functions";
import { isEmpty } from "@/utils/query-string";
import { onlyAlphanumericAndSpaces } from "@/utils/reg-expressions";
import { useRoles } from "@/hooks/useRoles";
import { MotiveTypes } from "@/constants/enums";

export const MotiveCreationPage = () => {
  const [isActive, setIsActive] = useState<boolean>(false);

  useEffect(() => {
    setIsActive(true);
  }, []);
  const [loadingTiposSolicitud, setLoadingTiposSolicitud] =
    useState<boolean>(false);

  const [loadingCreateMotive, setLoadingCreateMotive] =
    useState<boolean>(false);

  const [tiposSolicitud, setTiposSolicitud] = useState<
    { id: string; descripcion: string }[]
  >([]);

  const {
    getValues,
    register,
    control,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(motivesSchema),
  });

  const fetchTiposSolicitud = async () => {
    try {
      setLoadingTiposSolicitud(true);

      const response = await getTiposSolicitud();

      setTiposSolicitud(response);
    } catch {
      // error silenciado — el componente queda con lista vacía
    } finally {
      setLoadingTiposSolicitud(false);
    }
  };

  const navigate = useNavigate();

  useEffect(() => {
    if (isActive) fetchTiposSolicitud();
  }, [isActive]);

  const { fetchPermissions } = useRoles();

  const handleCreateMotive = async () => {
    handleCreateMotiveHandler({
      getValues,
      navigate,
      setLoadingCreateMotive,
      fetchPermissions,
    });
  };

  const handleCancel = () => {
    navigate(AppRoutes.MOTIVES);
  };

  const watchedValues = useWatch({
    control,
  });

  const hasNoFilledValues = useMemo(() => {
    const { tipoSolicitud, tipoMotivo, descripcion } = watchedValues || {};

    return (
      isEmpty(tipoSolicitud) || isEmpty(tipoMotivo) || isEmpty(descripcion)
    );
  }, [watchedValues]);

  return (
    <Flex flexDir="column" paddingTop="20px" gap="12px">
      <SectionHeader
        title="Agregar motivo"
        addButtonLabel="Agregar"
        cancelButtonLabel="Cancelar"
        handleCancel={handleCancel}
        handleAdd={handleCreateMotive}
        addButtonType="submit"
        disableAdd={
          loadingTiposSolicitud || hasNoFilledValues || loadingCreateMotive
        }
        loading={loadingCreateMotive}
      />

      <Card.Root maxW="full" marginTop="30px" borderRadius="8px">
        <Card.Body
          paddingX="48px"
          paddingY="30px"
          borderRadius="8px"
          border={`1px solid ${AppColors.WHITE_BORDER_1}`}
        >
          <Grid templateColumns="repeat(3, 1fr)">
            <GridItem paddingX="16px" paddingY="8px">
              <SelectComponent
                minHeight="40px"
                multiple
                items={[
                  {
                    label: "Rechazo",
                    value: MotiveTypes.Rechazo,
                  },
                  {
                    label: "Requiere información",
                    value: MotiveTypes.RequiereInformacion,
                  },
                  {
                    label: "Pedido no entregado",
                    value: MotiveTypes.PedidoNoEntregado,
                  },
                  {
                    label: "Visita a campo",
                    value: MotiveTypes.VisitaACampo,
                  },
                ]}
                control={control}
                name="tipoMotivo"
                labelText="Tipo"
                clearable
                closeOnSelect
                required
                disabled={loadingCreateMotive}
                placeholder="Seleccionar clase"
              />
            </GridItem>
            <GridItem paddingX="16px" paddingY="8px">
              <SelectComponent
                minHeight="40px"
                labelText="Tipo de solicitud"
                control={control}
                name="tipoSolicitud"
                multiple
                placeholder="Buscar..."
                items={tiposSolicitud.map((tipo) => ({
                  label: tipo?.descripcion,
                  value: tipo?.id,
                }))}
                required
                disabled={loadingCreateMotive || loadingTiposSolicitud}
                loading={loadingTiposSolicitud}
                closeOnSelect={false}
              />
            </GridItem>
          </Grid>
          <Flex gap={"50px"}></Flex>
        </Card.Body>
      </Card.Root>
      <Flex flexDir="column" gap="12px">
        <Text fontWeight={700} fontSize="20px">
          Descripción
        </Text>
        <TextArea
          errors={errors}
          inputName="descripcion"
          maxLength={30}
          register={register}
          placeholder="Ingresar una descripción del motivo"
          helperText="Máximo 30 caracteres"
          disabled={loadingCreateMotive}
          allowedPattern={onlyAlphanumericAndSpaces}
        />
      </Flex>
    </Flex>
  );
};
